// BUG-519: the discount code guessing lock (owner 2026-10-20: "if more than 6 attempts in 1 min, can
// we disable the field so we will not incur cost"). The webshop API enforces it per caller: six failed
// codes within a minute lock the caller for a minute (429 DISCOUNT_CODE_TOO_MANY_ATTEMPTS). The shop
// counts the same failures itself so it never sends the seventh, takes the server's word when it says
// the caller is locked, and disables the field with a countdown until the lock lifts.
// Contract: helinhair-webshop docs/decisions.md "2026-10-20: Discount code guessing lock".

import { useEffect } from 'react'
import { create } from 'zustand'
import { ApiError } from './shopApi'
import type { DiscountErrorCode } from './discountCode'

export const MAX_FAILED_ATTEMPTS = 6
export const ATTEMPT_WINDOW_MS = 60_000
export const LOCK_MS = 60_000
export const TOO_MANY_ATTEMPTS = 'DISCOUNT_CODE_TOO_MANY_ATTEMPTS'

/** Failures since `windowStart`; `lockedUntil` (epoch ms) is 0 while not locked. */
export interface LockState {
  windowStart: number
  failures: number
  lockedUntil: number
}

export const UNLOCKED: LockState = { windowStart: 0, failures: 0, lockedUntil: 0 }

/** Every refusal counts but "under the minimum": that code is real, the basket is just too small. */
export function countsAsFailedAttempt(code: DiscountErrorCode): boolean {
  return code !== 'DISCOUNT_CODE_MINIMUM_NOT_MET'
}

/** One more failed code at `now`; the sixth within a minute of the first locks for a minute. */
export function afterFailure(s: LockState, now: number): LockState {
  if (s.lockedUntil > now) return s
  const fresh = s.failures === 0 || s.lockedUntil !== 0 || now - s.windowStart >= ATTEMPT_WINDOW_MS
  const failures = fresh ? 1 : s.failures + 1
  return {
    windowStart: fresh ? now : s.windowStart,
    failures,
    lockedUntil: failures >= MAX_FAILED_ATTEMPTS ? now + LOCK_MS : 0,
  }
}

/** The server says the caller is locked for `seconds`: take that, never shortening a longer lock. */
export function lockedFor(s: LockState, now: number, seconds: number): LockState {
  const until = now + seconds * 1000
  return until > s.lockedUntil ? { ...s, lockedUntil: until } : s
}

/** Whole seconds until the field opens again; 0 when it is open. */
export function secondsLeft(s: LockState, now: number): number {
  return Math.max(0, Math.ceil((s.lockedUntil - now) / 1000))
}

/**
 * The seconds the server locked the caller for, from a 429 DISCOUNT_CODE_TOO_MANY_ATTEMPTS (60 when it
 * names none) or from the 400 of the failure that locked it (`retryAfterSeconds`); null otherwise.
 */
export function lockedSecondsOf(err: unknown): number | null {
  if (!(err instanceof ApiError)) return null
  const p = err.problem as { code?: unknown; retryAfterSeconds?: unknown } | null
  const seconds = typeof p?.retryAfterSeconds === 'number' && p.retryAfterSeconds > 0 ? p.retryAfterSeconds : null
  if (err.status === 429) return p?.code === TOO_MANY_ATTEMPTS ? (seconds ?? LOCK_MS / 1000) : null
  return seconds
}

interface LockStore {
  lock: LockState
  /** When the lock was last looked at (epoch ms): set by the actions and the countdown, never in render. */
  now: number
  /** A failed code just now. */
  fail: () => void
  /** The server locked this caller for `seconds` from now. */
  lockFor: (seconds: number) => void
  /** The countdown's second has passed. */
  tick: () => void
}

/** One lock per page, shared by the basket and checkout fields (the server counts per caller, too). */
export const useDiscountLock = create<LockStore>()((set) => ({
  lock: UNLOCKED,
  now: 0,
  fail: () =>
    set((s) => {
      const now = Date.now()
      return { lock: afterFailure(s.lock, now), now }
    }),
  lockFor: (seconds) =>
    set((s) => {
      const now = Date.now()
      return { lock: lockedFor(s.lock, now, seconds), now }
    }),
  tick: () => set({ now: Date.now() }),
}))

/** Seconds the field stays disabled, re-rendering each second while locked; 0 when open. */
export function useDiscountLockSeconds(): number {
  const lockedUntil = useDiscountLock((s) => s.lock.lockedUntil)
  const now = useDiscountLock((s) => s.now)
  const remaining = lockedUntil - now
  useEffect(() => {
    if (remaining <= 0) return
    const timer = setTimeout(() => useDiscountLock.getState().tick(), remaining % 1000 || 1000)
    return () => clearTimeout(timer)
  }, [remaining])
  return secondsLeft({ ...UNLOCKED, lockedUntil }, now)
}
